import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import { ImCancelCircle } from 'react-icons/im';
import '../../../Styles/Modal.css';
import '../../../Styles/reModal.css';

interface Product {
  _id: string;
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  selling: number;
  quantity: string;
}

interface ProductSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (product: { id: string; name: string; selling: number }) => void;
  customerID: string;
}

const ProductSelectModal: React.FC<ProductSelectModalProps> = ({ open, onClose, onSelect, customerID }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!customerID) {
      setError('No customer selected');
      return;
    }

    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired, please log in again.');
      setIsLoading(false);
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.get(`https://apibiz.oneflaretech.com/api/customer`, { headers });
      const bankData = response.data.data;
      const customer = bankData.find((customer: any) => customer._id === customerID);
      if (customer) {
        const uniqueProducts: Product[] = Array.from(
          customer.customerAccount
            .flatMap((account: any) => (account.product as Product[] || []))
            .reduce((map: Map<string, Product>, product: Product) => {
              if (!map.has(product._id)) {
                map.set(product._id, product);
              }
              return map;
            }, new Map<string, Product>())
            .values()
        );
        setProducts(uniqueProducts);
      } else {
        setError('Customer not found');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch products.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [customerID]);

  useEffect(() => {
    if (open && customerID) {
      fetchProducts();
    }
  }, [open, customerID, fetchProducts]);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleSelect = () => {
    if (selectedProduct) {
      onSelect({ id: selectedProduct._id, name: selectedProduct.name, selling: selectedProduct.selling });
      onClose();
      console.log("Selected product:", selectedProduct);
    }
  };

  if (!open) return null;

  return (
    <div className="modal">
      <ToastContainer />
      <div className="catModal">
        <div className="catModalTop">
          <h4>Product</h4>
          <ImCancelCircle className="catCancel" onClick={onClose} />
        </div>
        <div className="line"></div>
        {isLoading && <CircularProgress style={{ display: 'block', margin: '20px auto' }} />}
        {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
        {!isLoading && !error && products.length === 0 && (
          <p style={{ textAlign: 'center' }}>No products available for this customer.</p>
        )}
        <div className="cusmm">
          {products.map((product) => {
            const isSelected = product._id === selectedProduct?._id;
            const customerClassName = isSelected ? "payT payDark" : "payT";
            return (
              <div
                key={product._id}
                className={customerClassName}
                onClick={() => handleProductClick(product)}
              >
                <p>{product.name}</p>
              </div>
            );
          })}
        </div>
        <div className="catModalBtn">
          <button
            className={selectedProduct ? "catBtn1" : "opBtn"}
            onClick={handleSelect}
            disabled={!selectedProduct}
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectModal;